import React, { useEffect, useMemo, useState } from "react";
import * as XLSX from "xlsx";
import { toast } from "react-toastify";
import { Button, Input } from "../../components/Common/AppOverlay";
import UnsavedChangesDialog from "../../components/Common/UnsavedChangesDialog";
import {
  getEnabledFormat,
  getFormatLabel,
  getSourceSystem,
  sourceSystems,
} from "./importDefinitionConfig";
import { postRawData } from "../../helpers/url-request";
import { getLoggedInUser } from "../../helpers/backend_helper";
import {
  BASE_URL,
  DOWNLOAD_IMPORT_DEFINITION_TEMPLATE,
  UPLOAD_IMPORT_DEFINITION_TEMPLATE,
} from "../../helpers/url_helper";
import "./DataTemplateCreate.scss";

const steps = [
  "Definition",
  "File Structure",
  "Field Mapping",
  "Import Rules",
  "Review",
];
const importDestinations = ["Engagements", "Clients"];
const fieldsByDestination = {
  Clients: [
    "Client Code",
    "Client Name",
    "Client Type",
    "Email",
    "Phone Number",
    "Industry",
    "External Client ID",
  ],
  Engagements: [
    "Engagement Code",
    "Engagement Name",
    "Work Type",
    "Client Code",
    "Client Name",
    "Client Type",
    "Industry",
    "Description",
    "Financial Year End",
    "Period End Date",
    "Engagement Partner",
    "Manager",
    "External Engagement ID",
  ],
  Contacts: ["Client Code", "Contact Name", "Email", "Phone Number", "Role"],
  Users: ["User Name", "Email", "First Name", "Last Name", "Role"],
};
const engagementRiskParameterFields = [
  {
    value: "riskComplexTransaction",
    label: "Risk: Complex transactions and estimates",
  },
  { value: "riskNewPartner", label: "Risk: New partner or manager" },
  { value: "riskNewClient", label: "Risk: New or first-year client" },
  {
    value: "riskHighRiskIndustry",
    label: "Risk: High-risk industry considerations",
  },
  { value: "riskGrowingConcern", label: "Risk: Going-concern issues" },
  {
    value: "riskPriorYearAdjustments",
    label: "Risk: Prior-year significant adjustments",
  },
  { value: "riskFederalFunding", label: "Risk: Government or federal funding" },
  {
    value: "riskPriorInspectionFindings",
    label: "Risk: Prior inspection findings",
  },
  { value: "riskPublicCompany", label: "Risk: Issuer or public company" },
  { value: "riskOtherRisks", label: "Risk: Other engagement risk factor" },
];
const sampleColumns = [
  "CLIENT_ID",
  "CLIENT_NAME",
  "ENG_NO",
  "ENG_DESC",
  "PERIOD_END",
  "PARTNER",
  "INTERNAL_REF",
];

const normalise = (value) =>
  String(value || "")
    .replace(/[^a-z0-9]/gi, "")
    .toLowerCase();

const suggestedField = (column, fields) => {
  const aliases = {
    clientid: "Client Code",
    clientcode: "Client Code",
    clientname: "Client Name",
    clienttype: "Client Type",
    industry: "Industry",
    industrytype: "Industry",
    description: "Description",
    engno: "Engagement Code",
    engagementid: "Engagement Code",
    engdesc: "Engagement Name",
    engagementdescription: "Description",
    engagementtype: "Work Type",
    worktype: "Work Type",
    financialyearend: "Financial Year End",
    finyearend: "Financial Year End",
    yearend: "Financial Year End",
    periodend: "Period End Date",
    partner: "Engagement Partner",
    riskcomplextransaction: "riskComplexTransaction",
    risknewpartner: "riskNewPartner",
    risknewclient: "riskNewClient",
    riskhighriskindustry: "riskHighRiskIndustry",
    riskgoingconcern: "riskGrowingConcern",
    riskprioryearadjustments: "riskPriorYearAdjustments",
    riskfederalfunding: "riskFederalFunding",
    riskpriorinspectionfindings: "riskPriorInspectionFindings",
    riskpubliccompany: "riskPublicCompany",
    riskotherrisks: "riskOtherRisks",
  };
  const name = normalise(column);
  return (
    fields.find((field) => normalise(field) === name) || aliases[name] || ""
  );
};

const isRequiredSourceColumn = (column) =>
  new Set([
    "clientid",
    "clientcode",
    "clientname",
    "engno",
    "engagementid",
    "engagementcode",
    "engdesc",
    "engagementname",
    "engagementtype",
    "worktype",
    "financialyearend",
    "finyearend",
    "yearend",
    "periodend",
    "periodenddate",
    "primarycontactemail",
  ]).has(normalise(column));

const createDefinitionKey = () =>
  globalThis.crypto?.randomUUID?.() ||
  `${Date.now()}-${Math.random().toString(36).slice(2)}`;

const defaultMappings = (headers, target) =>
  headers.map((source) => ({
    source,
    sample:
      source === "CLIENT_ID"
        ? "C10021"
        : source === "ENG_NO"
        ? "E24001"
        : "Example value",
    destination: suggestedField(source, fieldsByDestination[target] || []),
    required: isRequiredSourceColumn(source),
  }));

const DataTemplateCreate = ({
  closeAction,
  onDraftSaved,
  onSaved,
  template,
}) => {
  const [step, setStep] = useState(1);
  const [draftId, setDraftId] = useState(template?.id);
  const [file, setFile] = useState(null);
  const [templateStorage, setTemplateStorage] = useState(
    template?.templateStorage || null
  );
  const [definitionKey] = useState(
    template?.definitionKey ||
      template?.templateStorage?.definitionKey ||
      createDefinitionKey
  );
  const [uploadingTemplate, setUploadingTemplate] = useState(false);
  const [loadingExistingTemplate, setLoadingExistingTemplate] = useState(false);
  const [existingTemplateLoadStage, setExistingTemplateLoadStage] =
    useState("");
  const [structureError, setStructureError] = useState("");
  const [errors, setErrors] = useState({});
  const [dirty, setDirty] = useState(false);
  const [confirmExit, setConfirmExit] = useState(false);
  const [mappingSearch, setMappingSearch] = useState("");
  const [unmappedOnly, setUnmappedOnly] = useState(false);
  const [requiredOnly, setRequiredOnly] = useState(false);
  const [form, setForm] = useState({
    name: template?.name || "",
    target: template?.target || "Engagements",
    sourceSystem: template?.sourceSystem || "CCH",
    formatType: template?.formatType || "XLSX",
    description: template?.description || "",
  });
  const [structure, setStructure] = useState(() => ({
    sheets: [],
    worksheet: "",
    headerRow: 1,
    dataStartRow: 2,
    headers: sampleColumns,
    preview: [],
    totalRows: 0,
    ignoreEmptyRows: true,
    trimValues: true,
    ignoreEmptyColumns: true,
    ...(template?.structure || {}),
  }));
  const [mappings, setMappings] = useState(() =>
    template?.mappings?.length
      ? template.mappings
      : defaultMappings(
          template?.structure?.headers?.length
            ? template.structure.headers
            : sampleColumns,
          template?.target || "Engagements"
        )
  );
  const [rules, setRules] = useState(() => ({
    matchField: "Engagement Code",
    existingAction: "Update existing record",
    invalidAction: "Skip invalid rows and continue",
    preserveBlanks: true,
    defaults: [
      { field: "Status", value: "Active" },
      { field: "Source", value: "CCH" },
    ],
    ...(template?.rules || {}),
  }));

  const sourceSystem = getSourceSystem(form.sourceSystem);
  const isEditMode = Boolean(template?.id);
  const formats = sourceSystem.formats;
  const destinationFields = fieldsByDestination[form.target] || [];
  const riskParameterFields =
    form.target === "Engagements" ? engagementRiskParameterFields : [];
  const mappedCount = mappings.filter((mapping) => mapping.destination).length;
  const requiredMappings = mappings.filter((mapping) => mapping.required);
  const requiredMapped = requiredMappings.filter(
    (mapping) => mapping.destination
  ).length;
  const isPhaseOneFormat = form.formatType === "XLSX";
  const currentFormat = formats.find(
    (format) => format.code === form.formatType
  );
  const filteredMappings = useMemo(
    () =>
      mappings.filter(
        (mapping) =>
          (!unmappedOnly || !mapping.destination) &&
          (!requiredOnly || mapping.required) &&
          `${mapping.source} ${mapping.sample} ${mapping.destination}`
            .toLowerCase()
            .includes(mappingSearch.toLowerCase())
      ),
    [mappingSearch, mappings, requiredOnly, unmappedOnly]
  );

  useEffect(() => {
    setRules((current) => ({
      ...current,
      defaults: current.defaults.map((item) =>
        item.field === "Source"
          ? { ...item, value: sourceSystem.shortLabel }
          : item
      ),
    }));
  }, [form.sourceSystem, sourceSystem.shortLabel]);

  useEffect(() => {
    setMappings((current) =>
      current.map((mapping) => ({
        ...mapping,
        required: isRequiredSourceColumn(mapping.source),
      }))
    );
  }, [structure.headers]);

  useEffect(() => {
    const warnBeforeUnload = (event) => {
      if (dirty) {
        event.preventDefault();
        event.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", warnBeforeUnload);
    return () => window.removeEventListener("beforeunload", warnBeforeUnload);
  }, [dirty]);

  const update = (key, value) => {
    setDirty(true);
    setForm((current) => ({ ...current, [key]: value }));
  };

  const updateStructure = (key, value) => {
    setDirty(true);
    setStructure((current) => ({ ...current, [key]: value }));
  };

  const updateRules = (next) => {
    setDirty(true);
    setRules((current) => ({ ...current, ...next }));
  };

  const changeSource = (sourceCode) => {
    setDirty(true);
    setForm((current) => ({
      ...current,
      sourceSystem: sourceCode,
      formatType: getEnabledFormat(sourceCode),
    }));
    setRules((current) => ({
      ...current,
      defaults: current.defaults.map((item) =>
        item.field === "Source"
          ? { ...item, value: getSourceSystem(sourceCode).shortLabel }
          : item
      ),
    }));
  };

  const autoMap = () => {
    setDirty(true);
    setMappings((current) =>
      current.map((mapping) => ({
        ...mapping,
        destination:
          suggestedField(mapping.source, destinationFields) ||
          mapping.destination,
      }))
    );
  };

  const clearMappings = () => {
    setDirty(true);
    setMappings((current) =>
      current.map((mapping) => ({ ...mapping, destination: "" }))
    );
  };

  const setMapping = (source, destination) => {
    setDirty(true);
    setMappings((current) =>
      current.map((mapping) =>
        mapping.source === source ? { ...mapping, destination } : mapping
      )
    );
  };

  const uploadTemplate = async (selectedFile) => {
    const formData = new FormData();
    formData.append("templateFile", selectedFile);
    formData.append("definitionKey", definitionKey);
    if (draftId || template?.id)
      formData.append("importDefinitionId", draftId || template.id);
    const response = await postRawData(
      UPLOAD_IMPORT_DEFINITION_TEMPLATE,
      "POST",
      formData
    );
    if (!response?.status)
      throw new Error(
        response?.message ||
          "PIERS could not store the template in Azure Blob Storage."
      );
    return response.data;
  };

  const analyseWorkbook = async (
    selectedFile,
    { upload = true, preserveMappings = false } = {}
  ) => {
    setStructureError("");
    if (!selectedFile || !/\.xlsx$/i.test(selectedFile.name)) {
      setStructureError("Upload an Excel .xlsx file.");
      return;
    }
    if (selectedFile.size > 10 * 1024 * 1024) {
      setStructureError("The file must be 10 MB or smaller.");
      return;
    }
    try {
      const workbook = XLSX.read(await selectedFile.arrayBuffer(), {
        type: "array",
      });
      const sheetName = workbook.SheetNames[0];
      const data = XLSX.utils.sheet_to_json(workbook.Sheets[sheetName], {
        header: 1,
        defval: "",
      });
      const headers = (data[0] || [])
        .map((header) => String(header).trim())
        .filter(Boolean);
      if (!workbook.SheetNames.length || !headers.length || data.length < 2)
        throw new Error(
          "The workbook needs a worksheet, header row and at least one data row."
        );
      if (upload) setUploadingTemplate(true);
      const storedTemplate = upload ? await uploadTemplate(selectedFile) : null;
      if (upload) setDirty(true);
      setFile(selectedFile);
      if (storedTemplate) setTemplateStorage(storedTemplate);
      setStructure((current) => ({
        ...current,
        sheets: workbook.SheetNames,
        worksheet:
          current.worksheet && workbook.SheetNames.includes(current.worksheet)
            ? current.worksheet
            : sheetName,
        headers,
        preview: data.slice(1, 6),
        totalRows: Math.max(data.length - 1, 0),
      }));
      if (!preserveMappings)
        setMappings(
          headers.map((source) => ({
            source,
            sample: String(data[1]?.[headers.indexOf(source)] || "—"),
            destination: suggestedField(source, destinationFields),
            required: isRequiredSourceColumn(source),
          }))
        );
    } catch (error) {
      setStructureError(error.message || "PIERS could not read this workbook.");
    } finally {
      if (upload) setUploadingTemplate(false);
    }
  };
  useEffect(() => {
    const blobName = template?.templateStorage?.blobName;
    const templateUrl = template?.templateUrl;
    if (!template?.id || (!blobName && !templateUrl)) return undefined;
    let isCurrent = true;

    const loadExistingTemplate = async () => {
      setLoadingExistingTemplate(true);
      setExistingTemplateLoadStage("Downloading saved workbook…");
      try {
        const url =
          templateUrl ||
          `${BASE_URL}${DOWNLOAD_IMPORT_DEFINITION_TEMPLATE}?blobName=${encodeURIComponent(
            blobName
          )}`;
        const response = await fetch(url, { credentials: "include" });
        if (!response.ok)
          throw new Error("The saved template file could not be loaded.");
        const fileName =
          template?.templateStorage?.fileName ||
          template?.file ||
          "import-template.xlsx";
        const existingFile = new File([await response.blob()], fileName, {
          type:
            template?.templateStorage?.contentType ||
            "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });
        if (isCurrent) {
          setExistingTemplateLoadStage("Reading workbook structure…");
          await new Promise((resolve) => window.requestAnimationFrame(resolve));
          if (isCurrent)
            await analyseWorkbook(existingFile, {
              upload: false,
              preserveMappings: Boolean(template?.mappings?.length),
            });
        }
      } catch (error) {
        if (isCurrent)
          setStructureError(
            `${
              error.message || "PIERS could not load the saved template."
            } Upload a replacement file to continue.`
          );
      } finally {
        if (isCurrent) {
          setLoadingExistingTemplate(false);
          setExistingTemplateLoadStage("");
        }
      }
    };
    loadExistingTemplate();
    return () => {
      isCurrent = false;
    };
  }, [
    template?.id,
    template?.templateUrl,
    template?.templateStorage?.blobName,
  ]);

  const validateStep = (stepToValidate = step) => {
    const nextErrors = {};
    if (stepToValidate === 1) {
      if (!form.name.trim()) nextErrors.name = "Definition Name is required.";
      if (!form.target)
        nextErrors.target = "Select where this data will be imported.";
      if (!form.sourceSystem) nextErrors.sourceSystem = "Select a source type.";
      if (!isPhaseOneFormat || !currentFormat?.enabled)
        nextErrors.formatType =
          "Excel (.xlsx) is the only currently supported format.";
    }
    if (
      stepToValidate === 2 &&
      (!file || structureError || !structure.headers.length)
    )
      nextErrors.file =
        structureError || "Upload a representative Excel file to continue.";
    if (stepToValidate === 3 && requiredMapped !== requiredMappings.length)
      nextErrors.mappings = `${
        requiredMappings.length - requiredMapped
      } required field${
        requiredMappings.length - requiredMapped === 1 ? " is" : "s are"
      } not mapped.`;
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const payload = (status) => ({
    ...template,
    ...form,
    id: draftId || template?.id,
    definitionKey,
    sourceType: sourceSystem.label,
    formatLabel: getFormatLabel(form.sourceSystem, form.formatType),
    file:
      templateStorage?.fileName ||
      file?.name ||
      template?.file ||
      "sample-import.xlsx",
    templateStorage,
    columns: mappings.length,
    mapped: mappedCount,
    required: `${requiredMapped} / ${requiredMappings.length}`,
    structure,
    mappings,
    rules,
    status,
  });

  const saveDraft = async () => {
    try {
      const saved = await onDraftSaved?.(payload("Draft"));
      if (saved?.id) setDraftId(saved.id);
      setDirty(false);
      toast.success("Import definition draft saved.");
    } catch (error) {
      toast.error(
        error.message || "Unable to save the import definition draft."
      );
    }
  };

  const createDefinition = async () => {
    const valid = [1, 2, 3].every((stepNumber) => validateStep(stepNumber));
    if (!valid) {
      toast.error(
        "Complete the highlighted items before creating this import definition."
      );
      return;
    }
    try {
      await onSaved?.(payload("Active"));
      setDirty(false);
      toast.success(
        template?.id
          ? "Import definition changes saved."
          : "Import definition created."
      );
    } catch (error) {
      toast.error(error.message || "Unable to save the import definition.");
    }
  };

  const advance = () => {
    if (validateStep()) setStep((current) => Math.min(current + 1, 5));
  };

  const requestExit = () => {
    if (dirty) setConfirmExit(true);
    else closeAction();
  };

  return (
    <section className="import-definition-workspace">
      <header className="import-definition-workspace__header">
        <div>
          <button
            className="import-definition-workspace__back-link"
            onClick={requestExit}
            type="button"
          >
            <i className="bx bx-left-arrow-alt" /> Import Definitions
          </button>
          <h1 className="piers-page-title">
            {template?.id
              ? `Edit Import Definition${form.name ? ` — ${form.name}` : ""}`
              : "Create Import Definition"}
          </h1>
          <p>
            Configure reusable source formats, file structures, field mappings
            and processing rules for importing data into PIERS.
          </p>
        </div>
        <div className="import-definition-workspace__header-actions">
          <Button
            className="import-definition-workspace__save-draft"
            outline
            onClick={saveDraft}
          >
            <i className="bx bx-save" /> Save Draft
          </Button>
          <button aria-label="Close" onClick={requestExit} type="button">
            <i className="bx bx-x" />
          </button>
        </div>
      </header>
      <nav
        className="import-definition-workspace__steps"
        aria-label="Import definition steps"
      >
        {steps.map((label, index) => {
          const stepNumber = index + 1;
          const complete = step > stepNumber;
          const canNavigate = isEditMode || stepNumber <= step;
          return (
            <button
              className={
                step === stepNumber
                  ? "is-active"
                  : complete
                  ? "is-complete"
                  : ""
              }
              disabled={!canNavigate}
              key={label}
              onClick={() => canNavigate && setStep(stepNumber)}
              type="button"
            >
              <span>
                {complete ? <i className="bx bx-check" /> : stepNumber}
              </span>
              {label}
            </button>
          );
        })}
      </nav>
      <main className="import-definition-workspace__body piers-page">
        {step === 1 && (
          <section className="import-definition-panel import-definition-panel--definition">
            <h2>Definition</h2>
            <p>
              Define what data this import definition will create or update.
            </p>
            <div className="import-definition-form">
              <label>
                Definition Name <b>*</b>
                <Input
                  className={errors.name ? "is-invalid" : ""}
                  maxLength="150"
                  onChange={(event) => update("name", event.target.value)}
                  placeholder="e.g. CCH Engagement Import"
                  value={form.name}
                />
                {errors.name && (
                  <small className="import-definition-field-error">
                    {errors.name}
                  </small>
                )}
              </label>
              <label>
                Import Data To <b>*</b>
                <Input
                  className={errors.target ? "is-invalid" : ""}
                  onChange={(event) => update("target", event.target.value)}
                  type="select"
                  value={form.target}
                >
                  {importDestinations.map((entity) => (
                    <option key={entity}>{entity}</option>
                  ))}
                </Input>
                {errors.target && (
                  <small className="import-definition-field-error">
                    {errors.target}
                  </small>
                )}
              </label>
              <label className="is-wide">
                Description
                <Input
                  maxLength="500"
                  onChange={(event) =>
                    update("description", event.target.value)
                  }
                  placeholder="Describe when this import definition should be used and what data it imports."
                  rows="3"
                  type="textarea"
                  value={form.description}
                />
              </label>
            </div>
            <section className="import-definition-section">
              <h3>Source & Format</h3>
              <p>
                Identify where the source data originates and the format PIERS
                will receive.
              </p>
              <div className="import-definition-form">
                <label>
                  Source Type <b>*</b>
                  <Input
                    className={errors.sourceSystem ? "is-invalid" : ""}
                    onChange={(event) => changeSource(event.target.value)}
                    type="select"
                    value={form.sourceSystem}
                  >
                    {sourceSystems.map((source) => (
                      <option key={source.code} value={source.code}>
                        {source.label}
                      </option>
                    ))}
                  </Input>
                  <small>{sourceSystem.description}</small>
                  {errors.sourceSystem && (
                    <small className="import-definition-field-error">
                      {errors.sourceSystem}
                    </small>
                  )}
                </label>
                <label>
                  Format Type <b>*</b>
                  <Input
                    className={errors.formatType ? "is-invalid" : ""}
                    onChange={(event) =>
                      update("formatType", event.target.value)
                    }
                    type="select"
                    value={form.formatType}
                  >
                    {formats.map((format) => (
                      <option
                        disabled={!format.enabled}
                        key={format.code}
                        value={format.code}
                      >
                        {format.label}
                      </option>
                    ))}
                  </Input>
                  {errors.formatType && (
                    <small className="import-definition-field-error">
                      {errors.formatType}
                    </small>
                  )}
                </label>
              </div>
              <aside className="import-definition-notice">
                <i className="bx bx-info-circle" />
                <div>
                  <b>Current release</b>
                  <span>
                    PIERS currently supports Excel (.xlsx) imports. Native and
                    additional structured formats can be enabled in future
                    releases.
                  </span>
                </div>
              </aside>
            </section>
          </section>
        )}
        {step === 2 && (
          <section className="import-definition-panel import-definition-panel--standard">
            <h2>File Structure</h2>
            <p>
              {template?.id
                ? "Review the saved template structure. Upload a file only to replace the existing template."
                : "Upload a representative Excel file and configure how PIERS should interpret its structure."}
            </p>
            <div className="import-definition-file-layout">
              <label className="import-definition-upload">
                <input
                  accept=".xlsx"
                  disabled={uploadingTemplate || loadingExistingTemplate}
                  onChange={(event) => analyseWorkbook(event.target.files?.[0])}
                  type="file"
                />
                <i
                  className={`bx ${
                    loadingExistingTemplate || uploadingTemplate
                      ? "bx-loader-alt bx-spin"
                      : "bx-cloud-upload"
                  }`}
                />
                <b>
                  {loadingExistingTemplate
                    ? "Loading saved template…"
                    : uploadingTemplate
                    ? "Storing template securely…"
                    : file
                    ? file.name
                    : "Drop Excel file here"}
                </b>
                <span aria-live="polite">
                  {loadingExistingTemplate
                    ? existingTemplateLoadStage
                    : uploadingTemplate
                    ? "Uploading to Azure Blob Storage"
                    : file
                    ? `${Math.round(file.size / 1024)} KB · ${
                        template?.id
                          ? "Saved template loaded · Choose a file to replace it"
                          : "Uploaded successfully"
                      }`
                    : "or Browse Files"}
                </span>
                <small>
                  Supported format: .xlsx · Maximum file size: 10 MB
                </small>
              </label>
              <aside className="import-definition-notice">
                <i className="bx bx-info-circle" />
                <div>
                  <b>File structure configuration</b>
                  <span>
                    {template?.id
                      ? "The saved workbook is loaded automatically. Upload a replacement only when its columns or layout have changed."
                      : "Choose a worksheet, set the header row and preview detected source columns after uploading a sample file."}
                  </span>
                </div>
              </aside>
            </div>
            {(structureError || errors.file) && (
              <aside className="import-definition-error">
                <i className="bx bx-error-circle" />
                {structureError || errors.file}
              </aside>
            )}
            {file && (
              <>
                <section className="import-definition-summary">
                  <strong>
                    1 <small>Worksheet Selected</small>
                  </strong>
                  <strong>
                    {structure.headers.length} <small>Columns Detected</small>
                  </strong>
                  <strong>
                    {structure.totalRows} <small>Sample Rows</small>
                  </strong>
                  <strong>
                    Row {structure.headerRow} <small>Header Row</small>
                  </strong>
                </section>
                <div className="import-definition-form import-definition-form--structure">
                  <label>
                    Worksheet <b>*</b>
                    <Input
                      onChange={(event) =>
                        updateStructure("worksheet", event.target.value)
                      }
                      type="select"
                      value={structure.worksheet}
                    >
                      {structure.sheets.map((sheet) => (
                        <option key={sheet}>{sheet}</option>
                      ))}
                    </Input>
                  </label>
                  <label>
                    Header Row <b>*</b>
                    <Input
                      min="1"
                      onChange={(event) =>
                        updateStructure("headerRow", Number(event.target.value))
                      }
                      type="number"
                      value={structure.headerRow}
                    />
                  </label>
                  <label>
                    Data Starts At
                    <Input
                      min="2"
                      onChange={(event) =>
                        updateStructure(
                          "dataStartRow",
                          Number(event.target.value)
                        )
                      }
                      type="number"
                      value={structure.dataStartRow}
                    />
                  </label>
                </div>
                <div className="import-definition-toggles">
                  {[
                    ["ignoreEmptyRows", "Ignore empty rows"],
                    ["trimValues", "Trim leading and trailing spaces"],
                    ["ignoreEmptyColumns", "Ignore completely empty columns"],
                  ].map(([key, label]) => (
                    <label key={key}>
                      <Input
                        checked={structure[key]}
                        onChange={(event) =>
                          updateStructure(key, event.target.checked)
                        }
                        type="checkbox"
                      />
                      {label}
                    </label>
                  ))}
                </div>
                <section className="import-definition-preview">
                  <b>
                    Data Preview{" "}
                    <small>
                      Showing first {structure.preview.length} rows of{" "}
                      {structure.totalRows}
                    </small>
                  </b>
                  <div>
                    <table>
                      <thead>
                        <tr>
                          {structure.headers.map((header) => (
                            <th key={header}>{header}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {structure.preview.map((row, index) => (
                          <tr key={index}>
                            {structure.headers.map((_, cellIndex) => (
                              <td key={cellIndex}>{row[cellIndex]}</td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </section>
              </>
            )}
          </section>
        )}
        {step === 3 && (
          <section className="import-definition-panel import-definition-panel--mapping">
            <h2>3. Field Mapping</h2>
            <p>Map source columns to the corresponding PIERS fields.</p>
            {riskParameterFields.length > 0 && (
              <aside className="import-definition-notice">
                <i className="bx bx-shield-quarter" />
                <div>
                  <b>Optional risk parameters</b>
                  <span>
                    Map any available Yes/No source columns to import the
                    related engagement risk selections.
                  </span>
                </div>
              </aside>
            )}
            <section className="import-definition-summary">
              <strong>
                {mappings.length} <small>Source Columns</small>
              </strong>
              <strong>
                {mappedCount} <small>Mapped</small>
              </strong>
              <strong>
                {requiredMappings.length - requiredMapped}{" "}
                <small>Required Remaining</small>
              </strong>
              <strong>
                {mappings.length - mappedCount} <small>Ignored</small>
              </strong>
            </section>
            <div className="import-definition-mapping-toolbar">
              <div className="import-definition-search">
                <i className="bx bx-search" />
                <Input
                  onChange={(event) => setMappingSearch(event.target.value)}
                  placeholder="Search source or PIERS fields..."
                  value={mappingSearch}
                />
              </div>
              <Button outline onClick={autoMap}>
                <i className="bx bx-magic-wand" /> Auto Map
              </Button>
              <Button outline onClick={clearMappings}>
                <i className="bx bx-eraser" /> Clear Mappings
              </Button>
              <label>
                <Input
                  checked={unmappedOnly}
                  onChange={(event) => setUnmappedOnly(event.target.checked)}
                  type="checkbox"
                />{" "}
                Show Unmapped Only
              </label>
              <label>
                <Input
                  checked={requiredOnly}
                  onChange={(event) => setRequiredOnly(event.target.checked)}
                  type="checkbox"
                />{" "}
                Show Required Only
              </label>
            </div>
            <section className="import-definition-mapping">
              <header>
                <span>Source Field</span>
                <span>Sample Value</span>
                <span>PIERS Field</span>
                <span>Data Type</span>
                <span>Required</span>
                <span>Transformation</span>
                <span>Status</span>
              </header>
              {filteredMappings.map((mapping) => (
                <div key={mapping.source}>
                  <span>{mapping.source}</span>
                  <span>{mapping.sample}</span>
                  <Input
                    onChange={(event) =>
                      setMapping(mapping.source, event.target.value)
                    }
                    type="select"
                    value={mapping.destination}
                  >
                    <option value="">Ignore</option>
                    {destinationFields.map((field) => (
                      <option key={field}>{field}</option>
                    ))}
                    {riskParameterFields.length > 0 && (
                      <optgroup label="Risk parameters">
                        {riskParameterFields.map((field) => (
                          <option key={field.value} value={field.value}>
                            {field.label}
                          </option>
                        ))}
                      </optgroup>
                    )}
                  </Input>
                  <span>
                    {/date|period/i.test(mapping.destination) ? "Date" : "Text"}
                  </span>
                  <span>{mapping.required ? "Yes" : "No"}</span>
                  <span>—</span>
                  <em className={mapping.destination ? "is-mapped" : ""}>
                    {mapping.destination ? "Mapped" : "Ignored"}
                  </em>
                </div>
              ))}
            </section>
            {errors.mappings && (
              <aside className="import-definition-error">
                <i className="bx bx-error-circle" />
                {errors.mappings}
              </aside>
            )}
          </section>
        )}
        {step === 4 && (
          <section className="import-definition-panel import-definition-panel--standard">
            <h2>4. Import Rules</h2>
            <p>
              Define how PIERS should identify, validate and process imported
              records.
            </p>
            <div className="import-definition-rules-grid">
              <section className="import-definition-section">
                <h3>Matching Rules</h3>
                <label>
                  Identify existing records by <b>*</b>
                  <Input
                    onChange={(event) =>
                      updateRules({ matchField: event.target.value })
                    }
                    type="select"
                    value={rules.matchField}
                  >
                    {destinationFields
                      .filter((field) => /code|id/i.test(field))
                      .map((field) => (
                        <option key={field}>{field}</option>
                      ))}
                  </Input>
                </label>
                <p>
                  PIERS uses this field to determine whether an imported row
                  creates a new record or matches an existing record.
                </p>
              </section>
              <section className="import-definition-section">
                <h3>Duplicate Handling</h3>
                {[
                  "Update existing record",
                  "Skip existing record",
                  "Report as conflict",
                ].map((option) => (
                  <label className="import-definition-radio" key={option}>
                    <Input
                      checked={rules.existingAction === option}
                      onChange={() => updateRules({ existingAction: option })}
                      type="radio"
                    />
                    {option}
                  </label>
                ))}
              </section>
              <section className="import-definition-section">
                <h3>Data Handling</h3>
                <label className="import-definition-radio">
                  <Input
                    checked={rules.preserveBlanks}
                    onChange={(event) =>
                      updateRules({ preserveBlanks: event.target.checked })
                    }
                    type="checkbox"
                  />
                  Do not overwrite existing PIERS values with blank imported
                  values
                </label>
                <label className="import-definition-radio">
                  <Input
                    checked={structure.ignoreEmptyRows}
                    onChange={(event) =>
                      updateStructure("ignoreEmptyRows", event.target.checked)
                    }
                    type="checkbox"
                  />
                  Ignore blank rows
                </label>
                <label className="import-definition-radio">
                  <Input
                    checked={structure.trimValues}
                    onChange={(event) =>
                      updateStructure("trimValues", event.target.checked)
                    }
                    type="checkbox"
                  />
                  Trim whitespace
                </label>
              </section>
              <section className="import-definition-section">
                <h3>Error Handling</h3>
                {[
                  "Skip invalid rows and continue",
                  "Stop import when validation errors are found",
                ].map((option) => (
                  <label className="import-definition-radio" key={option}>
                    <Input
                      checked={rules.invalidAction === option}
                      onChange={() => updateRules({ invalidAction: option })}
                      type="radio"
                    />
                    {option}
                  </label>
                ))}
              </section>
            </div>
            <section className="import-definition-section">
              <h3>Default Values</h3>
              {rules.defaults.map((item) => (
                <div className="import-definition-default" key={item.field}>
                  <span>{item.field}</span>
                  <span>{item.value}</span>
                </div>
              ))}
            </section>
          </section>
        )}
        {step === 5 && (
          <section className="import-definition-panel import-definition-panel--standard">
            <h2>5. Review Import Definition</h2>
            <p>
              Review the configuration before activating it for data imports.
            </p>
            <section className="import-definition-review">
              <header>
                <div>
                  <h3>{form.name || "Untitled Import Definition"}</h3>
                  <span>{form.target}</span>
                  <span>{sourceSystem.shortLabel}</span>
                  <span>
                    {getFormatLabel(form.sourceSystem, form.formatType)}
                  </span>
                </div>
                <em>
                  {requiredMapped === requiredMappings.length
                    ? "Ready to create"
                    : "Needs attention"}
                </em>
              </header>
              <div className="import-definition-review__grid">
                <section>
                  <h3>Definition</h3>
                  <span>
                    Import Data To <b>{form.target}</b>
                  </span>
                  <span>
                    Description <b>{form.description || "Not provided"}</b>
                  </span>
                  <span>
                    Source Type <b>{sourceSystem.label}</b>
                  </span>
                  <span>
                    Format Type{" "}
                    <b>{getFormatLabel(form.sourceSystem, form.formatType)}</b>
                  </span>
                </section>
                <section>
                  <h3>File Structure</h3>
                  <span>
                    Worksheet <b>{structure.worksheet || "Not configured"}</b>
                  </span>
                  <span>
                    Header Row <b>{structure.headerRow}</b>
                  </span>
                  <span>
                    Data Starts At <b>Row {structure.dataStartRow}</b>
                  </span>
                  <span>
                    Source Fields <b>{mappings.length}</b>
                  </span>
                </section>
                <section>
                  <h3>Field Mapping</h3>
                  <span>
                    Mapped Fields <b>{mappedCount}</b>
                  </span>
                  <span>
                    Unmapped Fields <b>{mappings.length - mappedCount}</b>
                  </span>
                  <span>
                    Required Fields{" "}
                    <b>
                      {requiredMapped} / {requiredMappings.length}
                    </b>
                  </span>
                  <button type="button" onClick={() => setStep(3)}>
                    View mappings
                  </button>
                </section>
                <section>
                  <h3>Import Rules</h3>
                  <span>
                    Match Existing By <b>{rules.matchField}</b>
                  </span>
                  <span>
                    Existing Records <b>{rules.existingAction}</b>
                  </span>
                  <span>
                    Invalid Records <b>{rules.invalidAction}</b>
                  </span>
                </section>
              </div>
            </section>
            <aside
              className={
                requiredMapped === requiredMappings.length
                  ? "import-definition-success"
                  : "import-definition-error"
              }
            >
              <i
                className={`bx ${
                  requiredMapped === requiredMappings.length
                    ? "bx-check-circle"
                    : "bx-error-circle"
                }`}
              />
              <div>
                <b>
                  {requiredMapped === requiredMappings.length
                    ? "Ready to create"
                    : "Items need attention"}
                </b>
                <span>
                  {requiredMapped === requiredMappings.length
                    ? "All required fields are mapped and the import definition can be created."
                    : "Complete required field mappings before creating this definition."}
                </span>
              </div>
            </aside>
          </section>
        )}
      </main>
      <footer className="import-definition-workspace__footer">
        <div
          className={`import-definition-workspace__footer-inner import-definition-workspace__footer-inner--${
            step === 1 ? "definition" : step === 3 ? "mapping" : "standard"
          }`}
        >
          <div>
            <Button outline onClick={requestExit}>
              Cancel
            </Button>
            <Button outline onClick={saveDraft}>
              <i className="bx bx-save" /> Save Draft
            </Button>
          </div>
          <div>
            {step > 1 && (
              <Button outline onClick={() => setStep((current) => current - 1)}>
                <i className="bx bx-left-arrow-alt" /> Back
              </Button>
            )}
            {step < 5 ? (
              <Button color="primary" onClick={advance}>
                Continue <i className="bx bx-right-arrow-alt" />
              </Button>
            ) : (
              <Button color="primary" onClick={createDefinition}>
                Create Import Definition <i className="bx bx-check" />
              </Button>
            )}
          </div>
        </div>
      </footer>
      <UnsavedChangesDialog
        isOpen={confirmExit}
        onContinue={() => setConfirmExit(false)}
        onDiscard={() => {
          setConfirmExit(false);
          setDirty(false);
          closeAction();
        }}
        subject="changes to this import definition"
      />
    </section>
  );
};

export default DataTemplateCreate;
